import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Building2, Filter, Store, Star, MessageSquare, Calendar, TrendingUp, ThumbsUp, ThumbsDown } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { KPICard } from '../components/ui/KPICard';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
function generateNormalDistribution(mean: number, stdDev: number, totalCount: number): Array<{
  rating: number;
  count: number;
}> {
  const data: Array<{
    rating: number;
    count: number;
  }> = [];
  const rawValues: number[] = [];
  for (let r = 10; r <= 50; r++) {
    const rating = r / 10;
    const exponent = -0.5 * Math.pow((rating - mean) / stdDev, 2);
    rawValues.push(Math.exp(exponent));
  }
  const rawSum = rawValues.reduce((a, b) => a + b, 0);
  for (let i = 0; i < rawValues.length; i++) {
    data.push({
      rating: (i + 10) / 10,
      count: Math.round(rawValues[i] / rawSum * totalCount)
    });
  }
  return data;
}
const countries = [{
  id: 'global',
  name: 'Global',
  icon: Building2
}, {
  id: 'deutschland',
  name: 'Deutschland'
}, {
  id: 'frankreich',
  name: 'Frankreich'
}, {
  id: 'spanien',
  name: 'Spanien'
}, {
  id: 'italien',
  name: 'Italien'
}, {
  id: 'grossbritannien',
  name: 'Großbritannien'
}, {
  id: 'polen',
  name: 'Polen'
}, {
  id: 'niederlande',
  name: 'Niederlande'
}];
const countryData: Record<string, {
  marketCount: number;
  avgRating: number;
  reviewCount: number;
  pieData: Array<{
    name: string;
    value: number;
  }>;
  barData: Array<{
    rating: number;
    count: number;
  }>;
}> = {
  global: {
    marketCount: 3250,
    avgRating: 4.0,
    reviewCount: 16800,
    pieData: [{
      name: '< 3.5',
      value: 1450
    }, {
      name: '3.5 - 4.0',
      value: 3200
    }, {
      name: '4.1 - 4.2',
      value: 3800
    }, {
      name: '4.3 - 4.4',
      value: 4600
    }, {
      name: '> 4.4',
      value: 3750
    }],
    barData: generateNormalDistribution(4.05, 0.6, 16800)
  },
  deutschland: {
    marketCount: 480,
    avgRating: 4.2,
    reviewCount: 2650,
    pieData: [{
      name: '< 3.5',
      value: 160
    }, {
      name: '3.5 - 4.0',
      value: 320
    }, {
      name: '4.1 - 4.2',
      value: 580
    }, {
      name: '4.3 - 4.4',
      value: 800
    }, {
      name: '> 4.4',
      value: 790
    }],
    barData: generateNormalDistribution(4.2, 0.5, 2650)
  },
  frankreich: {
    marketCount: 560,
    avgRating: 3.9,
    reviewCount: 2900,
    pieData: [{
      name: '< 3.5',
      value: 310
    }, {
      name: '3.5 - 4.0',
      value: 580
    }, {
      name: '4.1 - 4.2',
      value: 720
    }, {
      name: '4.3 - 4.4',
      value: 780
    }, {
      name: '> 4.4',
      value: 510
    }],
    barData: generateNormalDistribution(3.95, 0.6, 2900)
  }
};
const getCountryData = (countryId: string) => {
  if (countryData[countryId]) return countryData[countryId];
  return {
    marketCount: 420,
    avgRating: 4.0,
    reviewCount: 2100,
    pieData: [{
      name: '< 3.5',
      value: 200
    }, {
      name: '3.5 - 4.0',
      value: 380
    }, {
      name: '4.1 - 4.2',
      value: 480
    }, {
      name: '4.3 - 4.4',
      value: 580
    }, {
      name: '> 4.4',
      value: 460
    }],
    barData: generateNormalDistribution(4.0, 0.55, 2100)
  };
};
const countryScorecard = [{
  country: 'Deutschland',
  avgRating: 4.2,
  markets: 480,
  reviews: 2650
}, {
  country: 'Frankreich',
  avgRating: 3.9,
  markets: 560,
  reviews: 2900
}, {
  country: 'Spanien',
  avgRating: 3.8,
  markets: 620,
  reviews: 3400
}, {
  country: 'Italien',
  avgRating: 4.1,
  markets: 390,
  reviews: 2150
}, {
  country: 'Großbritannien',
  avgRating: 4.0,
  markets: 450,
  reviews: 2080
}, {
  country: 'Polen',
  avgRating: 4.1,
  markets: 490,
  reviews: 2300
}, {
  country: 'Niederlande',
  avgRating: 4.3,
  markets: 360,
  reviews: 1550
}];
const timePeriods = [{
  id: '30',
  label: '30 Tage'
}, {
  id: '60',
  label: '60 Tage'
}, {
  id: '120',
  label: '120 Tage'
}, {
  id: '365',
  label: '365 Tage'
}, {
  id: 'gesamt',
  label: 'Gesamt'
}];
function applyTimeFactor(value: number, period: string, isRating: boolean = false): number {
  if (period === 'gesamt') return value;
  if (isRating) {
    const noise = period === '30' ? 0.1 : period === '60' ? -0.1 : period === '120' ? 0.05 : -0.05;
    return Math.max(1, Math.min(5, value + noise));
  }
  const factors: Record<string, number> = {
    '30': 0.08,
    '60': 0.17,
    '120': 0.33,
    '365': 1.0
  };
  return Math.max(1, Math.round(value * (factors[period] || 1)));
}
function getRatingColor(r: number): string {
  if (r > 4.4) return '#639754';
  if (r >= 4.3) return '#8BC34A';
  if (r >= 4.1) return '#FFF000';
  if (r >= 3.5) return '#FF9800';
  return '#E60A0A';
}
const PIE_COLORS = ['#E60A0A', '#FF9800', '#FFF000', '#8BC34A', '#639754'];
export function KonzernUebersichtPage() {
  const [selectedCountry, setSelectedCountry] = useState('global');
  const [selectedPeriod, setSelectedPeriod] = useState('gesamt');
  const rawData = getCountryData(selectedCountry);
  const currentData = {
    ...rawData,
    avgRating: applyTimeFactor(rawData.avgRating, selectedPeriod, true),
    reviewCount: applyTimeFactor(rawData.reviewCount, selectedPeriod),
    pieData: rawData.pieData.map(d => ({
      ...d,
      value: applyTimeFactor(d.value, selectedPeriod)
    })),
    barData: rawData.barData.map(d => ({
      ...d,
      count: applyTimeFactor(d.count, selectedPeriod)
    }))
  };
  const scaledScorecard = countryScorecard.map(item => ({
    ...item,
    avgRating: applyTimeFactor(item.avgRating, selectedPeriod, true),
    reviews: applyTimeFactor(item.reviews, selectedPeriod)
  }));
  return <div className="p-6 space-y-6 overflow-y-auto h-[calc(100vh-64px)]" data-id="element-678">
      <Card className="!p-4" data-id="element-679">
        <div className="flex flex-col gap-4" data-id="element-680">
          <div className="flex items-center gap-4 flex-wrap" data-id="element-681">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700" data-id="element-682">
              <Filter size={16} data-id="element-683" />
              <span data-id="element-684">Land:</span>
            </div>
            <div className="flex flex-wrap gap-2" data-id="element-685">
              {countries.map(country => {
              const isSelected = selectedCountry === country.id;
              const Icon = country.icon;
              return <button key={country.id} onClick={() => setSelectedCountry(country.id)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${isSelected ? 'bg-lidl-blue text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-686">
                    {Icon && <Icon size={14} data-id="element-687" />}
                    {country.name}
                  </button>;
            })}
            </div>
          </div>
          <div className="flex items-center gap-4 flex-wrap" data-id="element-688">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700" data-id="element-689">
              <Calendar size={16} data-id="element-690" />
              <span data-id="element-691">Zeitraum:</span>
            </div>
            <div className="flex flex-wrap gap-2" data-id="element-692">
              {timePeriods.map(period => <button key={period.id} onClick={() => setSelectedPeriod(period.id)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${selectedPeriod === period.id ? 'bg-lidl-blue text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-693">
                  {period.label}
                </button>)}
            </div>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-694">
        <KPICard title="Durchschnittliche Bewertung" value={currentData.avgRating.toFixed(1)} icon={<TrendingUp size={18} data-id="element-696" />} data-id="element-695" />
        <KPICard title="Gesamtbewertungen" value={currentData.reviewCount.toLocaleString('de-DE')} icon={<MessageSquare size={18} data-id="element-698" />} data-id="element-697" />
        <KPICard title="Positive Resonanz" value="64%" icon={<ThumbsUp size={18} data-id="element-700" />} data-id="element-699" />
        <KPICard title="Negative Resonanz" value="12%" icon={<ThumbsDown size={18} data-id="element-702" />} data-id="element-701" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-703">
        <Card data-id="element-704">
          <div className="p-6" data-id="element-705">
            <h3 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-706">
              Bewertungsverteilung
            </h3>
            <p className="text-sm text-gray-500 mb-6" data-id="element-707">
              Verteilung der Google Bewertungen nach Sterne-Kategorien
            </p>
            <div className="h-96" data-id="element-708">
              <ResponsiveContainer width="100%" height="100%" data-id="element-709">
                <PieChart data-id="element-710">
                  <Pie data={currentData.pieData} cx="50%" cy="60%" innerRadius={90} outerRadius={140} paddingAngle={2} dataKey="value" data-id="element-711">
                    {currentData.pieData.map((entry, index) => <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} data-id="element-712" />)}
                  </Pie>
                  <Tooltip contentStyle={{
                  borderRadius: '8px',
                  border: 'none',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  fontSize: '12px'
                }} formatter={(value: number) => [value.toLocaleString('de-DE') + ' Bewertungen', 'Anzahl']} data-id="element-713" />
                  <Legend layout="vertical" align="right" verticalAlign="middle" iconType="circle" iconSize={10} wrapperStyle={{
                  fontSize: '13px',
                  lineHeight: '2'
                }} formatter={(value: string) => <span style={{
                  color: '#1f2937',
                  fontWeight: 500
                }} data-id="element-715">
                        {value}
                      </span>} data-id="element-714" />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </Card>

        <Card data-id="element-716">
          <div className="p-6" data-id="element-717">
            <h3 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-718">
              Bewertungen nach Sternen
            </h3>
            <p className="text-sm text-gray-500 mb-6" data-id="element-719">
              Verteilung der Bewertungen (1,0 bis 5,0 Sterne)
            </p>
            <div className="h-80" data-id="element-720">
              <ResponsiveContainer width="100%" height="100%" data-id="element-721">
                <BarChart data={currentData.barData} margin={{
                top: 20,
                right: 30,
                left: 0,
                bottom: 5
              }} data-id="element-722">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} data-id="element-723" />
                  <XAxis dataKey="rating" type="number" domain={[1, 5]} ticks={[1.0, 1.5, 2.0, 2.5, 3.0, 3.5, 4.0, 4.5, 5.0]} axisLine={false} tickLine={false} tick={{
                  fontSize: 11,
                  fill: '#6b7280'
                }} tickFormatter={(value: number) => value.toFixed(1).replace('.', ',')} data-id="element-724" />
                  <YAxis axisLine={false} tickLine={false} tick={{
                  fontSize: 12,
                  fill: '#9ca3af'
                }} tickFormatter={(value: number) => value >= 1000 ? `${(value / 1000).toFixed(1)}k` : String(value)} data-id="element-725" />
                  <Tooltip cursor={{
                  fill: '#f3f4f6'
                }} contentStyle={{
                  borderRadius: '8px',
                  border: 'none',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  fontSize: '12px'
                }} labelFormatter={(label: number) => `${label.toFixed(1).replace('.', ',')} Sterne`} formatter={(value: number) => [value.toLocaleString('de-DE'), 'Bewertungen']} data-id="element-726" />
                  <Bar dataKey="count" radius={[2, 2, 0, 0]} data-id="element-727">
                    {currentData.barData.map((entry, index) => {
                    const r = entry.rating;
                    let color = '#E60A0A';
                    if (r > 4.4) color = '#639754';else if (r >= 4.3) color = '#8BC34A';else if (r >= 4.1) color = '#FFF000';else if (r >= 3.5) color = '#FF9800';
                    return <Cell key={`cell-${index}`} fill={color} data-id="element-728" />;
                  })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </Card>
      </div>

      <Card data-id="element-729">
        <div className="p-6" data-id="element-730">
          <h3 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-731">
            Länderübersicht
          </h3>
          <p className="text-sm text-gray-500 mb-6" data-id="element-732">
            Kennzahlenvergleich aller LIDL-Länder
          </p>
          <div className="space-y-3" data-id="element-733">
            {[...scaledScorecard].sort((a, b) => b.avgRating - a.avgRating).map((item, index) => {
            const color = getRatingColor(item.avgRating);
            const barWidth = (item.avgRating - 1.0) / 4.0 * 100;
            const isTop = index === 0;
            return <motion.div key={item.country} initial={{
              opacity: 0,
              x: -12
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: index * 0.06
            }} className={`rounded-xl p-4 border ${isTop ? 'border-[#639754]/30 bg-[#f0f7ed]' : 'border-gray-100 bg-gray-50/50'}`} data-id="element-734">
                    <div className="flex items-center justify-between mb-2" data-id="element-735">
                      <div className="flex items-center gap-2.5" data-id="element-736">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold" style={{
                    backgroundColor: color
                  }} data-id="element-737">
                          {index + 1}
                        </div>
                        <span className="text-sm font-semibold text-gray-900" data-id="element-738">
                          {item.country}
                        </span>
                      </div>
                      <div className="flex items-center gap-4" data-id="element-739">
                        <div className="text-right" data-id="element-740">
                          <div className="text-xs text-gray-400" data-id="element-741">Filialen</div>
                          <div className="text-sm font-semibold text-gray-700" data-id="element-742">
                            {item.markets.toLocaleString('de-DE')}
                          </div>
                        </div>
                        <div className="text-right" data-id="element-743">
                          <div className="text-xs text-gray-400" data-id="element-744">
                            Bewertungen
                          </div>
                          <div className="text-sm font-semibold text-gray-700" data-id="element-745">
                            {item.reviews.toLocaleString('de-DE')}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3" data-id="element-746">
                      <div className="flex-1 h-6 bg-gray-200/60 rounded-full overflow-hidden" data-id="element-747">
                        <motion.div initial={{
                    width: 0
                  }} animate={{
                    width: `${Math.min(barWidth, 100)}%`
                  }} transition={{
                    duration: 0.6,
                    delay: index * 0.06 + 0.2,
                    ease: 'easeOut'
                  }} className="h-full rounded-full relative" style={{
                    backgroundColor: color
                  }} data-id="element-748">
                          {barWidth > 30 && <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs font-bold text-white" data-id="element-749">
                              {item.avgRating.toFixed(1).replace('.', ',')} ★
                            </span>}
                        </motion.div>
                      </div>
                      {barWidth <= 30 && <span className="text-sm font-bold whitespace-nowrap" style={{
                  color
                }} data-id="element-750">
                          {item.avgRating.toFixed(1).replace('.', ',')} ★
                        </span>}
                    </div>
                  </motion.div>;
          })}
          </div>
        </div>
      </Card>
    </div>;
}